/* ===== External Libraries ===== */
import { AXIOS, CallbackErrorAxios, CallbackWarningAxios, CommonGlobalService, UUID } from '@umanforge/common';
import { AxiosError, AxiosResponse } from 'axios';
import { Dispatch } from '@reduxjs/toolkit';

/* ===== API Configuration ===== */
import { API } from 'constants/api.constant';

/* ===== Types and Interfaces ===== */
import { ChartOfSectionApi, CreateChartOfSectionPayload, UpdateChartOfSectionPayload } from 'interfaces/chartOfSection.interface';
import type { ChartWithDataApi } from 'interfaces/chartWithData.interface';

/* ===== Services ===== */
import { GlobalService } from './global.service';

export class ChartOfSectionService {


  /* ===== Update chart setting on section ===== */
  static updateChartOnSection(
    chartInfoId: UUID,
    body: UpdateChartOfSectionPayload,
    token: string | null, dispatch: Dispatch,
    onError?: CallbackErrorAxios, onWarning?: CallbackWarningAxios
  ): Promise<boolean> {
    return new Promise( async resolve => {
      AXIOS.put( `${API.CHART_OF_SECTION}/${chartInfoId}`, body, await CommonGlobalService.getHeaderRequest( token ) )
        .then( ( response: AxiosResponse ) => {
          CommonGlobalService.saveRequestToken( response, dispatch );
          if ( response.status === 202 ) {
            GlobalService.onAxiosWarning( response, onWarning );
          }
          resolve( response.status === 200 || response.status === 201 );
        } )
        .catch( ( err: AxiosError ) => {
          GlobalService.onAxiosError( err, dispatch, onError );
        } );
    } );
  }

  /* ===== Delete chart setting on section ===== */
  static deleteChartOnSection(
    chartInfoId: UUID,
    token: string | null, dispatch: Dispatch,
    onError?: CallbackErrorAxios, onWarning?: CallbackWarningAxios
  ): Promise<boolean> {
    return new Promise( async resolve => {
      AXIOS.delete( `${API.CHART_OF_SECTION}/${chartInfoId}`, await CommonGlobalService.getHeaderRequest( token ) )
        .then( ( response: AxiosResponse ) => {
          CommonGlobalService.saveRequestToken( response, dispatch );
          if ( response.status === 202 ) {
            GlobalService.onAxiosWarning( response, onWarning );
          }
          resolve( response.status === 200 || response.status === 201 );
        } )
        .catch( ( err: AxiosError ) => {
          GlobalService.onAxiosError( err, dispatch, onError );
        } );
    } );
  }

  /* ===== Get chart settings by section ===== */
  static getBySection(
    chartSectionId: UUID,
    token: string | null, dispatch: Dispatch,
    onError?: CallbackErrorAxios, onWarning?: CallbackWarningAxios
  ): Promise<ChartOfSectionApi[]> {
    return new Promise( async resolve => {
      AXIOS.get( `${API.CHART_OF_SECTION}/${chartSectionId}`, await CommonGlobalService.getHeaderRequest( token ) )
        .then( ( response: AxiosResponse<ChartOfSectionApi[]> ) => {
          CommonGlobalService.saveRequestToken( response, dispatch );
          if ( response.status === 202 ) {
            GlobalService.onAxiosWarning( response, onWarning );
          }
          resolve( response.data );
        } )
        .catch( ( err: AxiosError ) => {
          GlobalService.onAxiosError( err, dispatch, onError );
        } );
    } );
  }

  /* ===== Get charts settings for visualistion of event manager ===== */
  static getVisualisationChartByEventManager(
    eventManagerId: UUID,
    token: string | null, dispatch: Dispatch,
    onError?: CallbackErrorAxios, onWarning?: CallbackWarningAxios
  ): Promise<ChartOfSectionApi[]> {
    return new Promise( async resolve => {
      AXIOS.get( `${API.CHART_OF_SECTION}/visualisation_by_event_manager/${eventManagerId}`, await CommonGlobalService.getHeaderRequest( token ) )
        .then( ( response: AxiosResponse<ChartOfSectionApi[]> ) => {
          CommonGlobalService.saveRequestToken( response, dispatch );
          if ( response.status === 202 ) {
            GlobalService.onAxiosWarning( response, onWarning );
          }
          resolve( response.data );
        } )
        .catch( ( err: AxiosError ) => {
          GlobalService.onAxiosError( err, dispatch, onError );
        } );
    } );
  }

  /* ===== Get charts settings for visualistion of event manager ===== */
  static manageVisualisationChartsByEventManager(
    eventManagerId: UUID,
    deletedChartsId: UUID[], addedChartsId: UUID[],
    token: string | null, dispatch: Dispatch,
    onError?: CallbackErrorAxios, onWarning?: CallbackWarningAxios
  ): Promise<boolean> {
    const body = {
      add: addedChartsId,
      delete: deletedChartsId
    };
    return new Promise( async resolve => {
      AXIOS.put( `${API.CHART_OF_SECTION}/visualisation_by_event_manager/${eventManagerId}`, body, await CommonGlobalService.getHeaderRequest( token ) )
        .then( ( response: AxiosResponse ) => {
          CommonGlobalService.saveRequestToken( response, dispatch );
          if ( response.status === 202 ) {
            GlobalService.onAxiosWarning( response, onWarning );
          }
          resolve( response.status === 201 );
        } )
        .catch( ( err: AxiosError ) => {
          GlobalService.onAxiosError( err, dispatch, onError );
        } );
    } );
  }

  /* ===== Create chart setting on section ===== */
  static createChartOnSection(
    body: CreateChartOfSectionPayload,
    token: string | null, dispatch: Dispatch,
    onError?: CallbackErrorAxios, onWarning?: CallbackWarningAxios
  ): Promise<UUID | undefined> {
    return new Promise( async resolve => {
      AXIOS.post( API.CHART_OF_SECTION, body, await CommonGlobalService.getHeaderRequest( token ) )
        .then( ( response: AxiosResponse<{ id: UUID }> ) => {
          CommonGlobalService.saveRequestToken( response, dispatch );
          if ( response.status === 202 ) {
            GlobalService.onAxiosWarning( response, onWarning );
          }
          resolve( response.data?.id );
        } )
        .catch( ( err: AxiosError ) => {
          GlobalService.onAxiosError( err, dispatch, onError );
        } );
    } );
  }

  /* ===== Update charts positions in section ===== */
  static updateChartsPositions(
    chartSectionId: UUID,
    positions: { id: UUID; position: number }[],
    token: string | null, dispatch: Dispatch,
    onError?: CallbackErrorAxios, onWarning?: CallbackWarningAxios
  ): Promise<boolean> {
    return new Promise( async resolve => {
      AXIOS.put(
        `${API.CHART_OF_SECTION}/positions/${chartSectionId}`,
        positions,
        await CommonGlobalService.getHeaderRequest( token )
      )
        .then( ( response: AxiosResponse ) => {
          CommonGlobalService.saveRequestToken( response, dispatch );
          if ( response.status === 202 ) {
            GlobalService.onAxiosWarning( response, onWarning );
          }
          resolve( response.status === 200 || response.status === 201 );
        } )
        .catch( ( err: AxiosError ) => {
          GlobalService.onAxiosError( err, dispatch, onError );
        } );
    } );
  }

  /* ===== Get chart configs + data by section ===== */
  static getChartDatasOfSection(
    chartSectionId: UUID,
    token: string | null, dispatch: Dispatch,
    onError?: CallbackErrorAxios, onWarning?: CallbackWarningAxios
  ): Promise<ChartWithDataApi[]> {
    return new Promise( async resolve => {
      AXIOS.get( `${API.CHART_OF_SECTION}/charts/${chartSectionId}`, await CommonGlobalService.getHeaderRequest( token ) )
        .then( ( response: AxiosResponse<ChartWithDataApi[]> ) => {
          CommonGlobalService.saveRequestToken( response, dispatch );
          if ( response.status === 202 ) {
            GlobalService.onAxiosWarning( response, onWarning );
          }
          resolve( response.data );
        } )
        .catch( ( err: AxiosError ) => {
          GlobalService.onAxiosError( err, dispatch, onError );
        } );
    } );
  }
}
